/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import { FileDTO, FileUpload } from '@app-libs/model';
import { BehaviorSubject, Observable } from 'rxjs';
import { createFileUploadObject } from '../../../utils/file-processing.utils';
import {
  extractFileFromDragEvent,
  extractFileFromInputEvent,
} from '../../../utils/input-handling.utils';
import { AuthService } from '../../auth/auth.service';
import { EventBusService, EventTypes } from '../../client/event-bus.service';
import { FileImportService } from '../../utils/file-import.service';
import { ChatApiService } from '../chat-api.service';
import { ChatStateService } from '../chat-state.service';
import { ChatNotificationService } from './chat-notification.service';

export interface FileUploadState {
  uploadedFile: FileUpload | undefined;
  isUploading: boolean;
  error: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  private fileUploadStateSubject = new BehaviorSubject<FileUploadState>({
    uploadedFile: undefined,
    isUploading: false,
    error: null,
  });

  public fileUploadState$ = this.fileUploadStateSubject.asObservable();

  constructor(
    private fileImportService: FileImportService,
    private authService: AuthService,
    private notificationService: ChatNotificationService,
    private eventBus: EventBusService,
    private chatApiService: ChatApiService,
    private chatStateService: ChatStateService,
  ) { }

  get currentState(): FileUploadState {
    return this.fileUploadStateSubject.value;
  }

  get uploadedFile(): FileUpload | undefined {
    return this.currentState.uploadedFile;
  }

  get isUploading(): boolean {
    return this.currentState.isUploading;
  }

  async handleFileFromInput(event: Event): Promise<void> {
    console.debug('handleFileFromInput called');
    const file = extractFileFromInputEvent(event);
    if (!file) {
      console.log('File selection was canceled');
      this.removeUploadedFile();
      return;
    }

    await this.handleFile(file);
  }

  async handleFileFromDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    const file = extractFileFromDragEvent(event);
    if (file) {
      await this.handleFile(file);
    }
  }

  async handleFile(file: File): Promise<void> {
    console.debug('Handling file:', file);
    if (!this.fileImportService.isValidFileType(file)) {
      this.setError('Unsupported file type');
      this.notificationService.showError(`Unsupported file type: ${file.type}`);
      return;
    }

    this.setUploading(true);
    this.clearError();

    try {
      // Always process the file locally first (for PDFs, images, etc.)
      const uploaded = await this.fileImportService.readFileContent(file, true);

      // Create the base file upload object
      const fileUpload = this.createFileUploadObject(uploaded, file);

      console.debug('File upload object created:', fileUpload);
      console.debug('File upload this.shouldUploadToOpenAI(file):', this.shouldUploadToOpenAI(file));

      // Check if we need to upload to OpenAI for Assistant API
      if (this.shouldUploadToOpenAI(file)) {
        try {
          console.debug('Uploading file to OpenAI for Assistant API:', file.name);
          const uploadResult = await this.chatApiService.uploadFileToOpenAI(file).toPromise();

          if (uploadResult?.fileId) {
            // Store the OpenAI file ID for Assistant API usage
            fileUpload.file_id_ext_ref = uploadResult.fileId;
            console.debug('OpenAI file upload successful:', uploadResult.fileId);
          }
        } catch (error) {
          console.warn('OpenAI file upload failed, using base64 fallback:', error);
          // Continue with local processing only - graceful degradation
        }
      }

      this.saveUploadFile(fileUpload);
    } catch (error) {
      const errorMessage = 'Error processing file';
      this.setError(errorMessage);
      this.notificationService.showError(errorMessage);
    } finally {
      this.setUploading(false);
    }
  }

  /**
   * Determines if a file should be uploaded to OpenAI backend
   * Currently uploads images when Assistant API is being used
   */
  private shouldUploadToOpenAI(file: File): boolean {
    console.debug('shouldUploadToOpenAI check:', {
      fileName: file.name,
      fileType: file.type,
      isImage: this.isImageFile(file)
    });

    // Only upload images for now
    if (!this.isImageFile(file)) {
      console.debug('Not uploading - not an image file');
      return false;
    }

    // Check if we're in an Assistant context that might need OpenAI file IDs
    const assistant = this.chatStateService.assistant;
    console.debug('Assistant context:', {
      hasAssistant: !!assistant,
      assistantId: assistant?.assistant_id,
      externalId: assistant?.external_id,
      assistantData: assistant
    });

    const isExternalAssistantContext = assistant && assistant.external_id;
    const shouldUpload = !!isExternalAssistantContext;

    console.debug('shouldUploadToOpenAI result:', shouldUpload);
    return shouldUpload;
  }

  /**
   * Checks if a file is an image
   */
  private isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  private handleFileContent(uploaded: Partial<FileDTO>, file: File): void {
    const fileUpload = this.createFileUploadObject(uploaded, file);
    this.saveUploadFile(fileUpload);
  }

  private createFileUploadObject(uploaded: Partial<FileDTO>, file: File): FileDTO {
    return createFileUploadObject(uploaded, file, this.authService.getCurrentUserId()!);
  }

  private saveUploadFile(fileUpload: FileUpload): void {
    console.debug('saveUploadFile', fileUpload);

    // Remove file_handle before storing to fix HTTP serialization issue
    const cleanFileUpload = { ...fileUpload };
    delete (cleanFileUpload as any).file_handle;

    this.updateState({
      uploadedFile: cleanFileUpload,
      isUploading: false,
      error: null,
    });

    // Also update the chat state so the UI can access it
    this.chatStateService.uploadedFile = cleanFileUpload;

    this.eventBus.emit({ type: EventTypes.FILE_UPLOADED, data: cleanFileUpload });
  }

  removeUploadedFile(): void {
    console.debug('removeUploadedFile');

    this.updateState({
      uploadedFile: undefined,
      isUploading: false,
      error: null,
    });

    // Also clear from chat state
    this.chatStateService.uploadedFile = undefined;

    this.eventBus.emit({ type: EventTypes.REMOVE_UPLOADED_FILE });
  }

  clearUploadFile(): void {
    console.debug('clearUploadFile');

    this.updateState({
      uploadedFile: undefined,
      isUploading: false,
      error: null,
    });

    // Also clear from chat state
    this.chatStateService.uploadedFile = undefined;
  }

  getFormattedFileName(fileName: string, isMobile = false): string {
    const maxLength = isMobile ? 30 : 80;
    
    if (fileName.length <= maxLength) {
      return fileName;
    }

    const extension =
      fileName.lastIndexOf('.') > -1 ? fileName.substring(fileName.lastIndexOf('.')) : '';

    const nameWithoutExt = extension ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName;

    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 3);

    return `${truncatedName}...${extension}`;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  private setUploading(isUploading: boolean): void {
    this.updateState({
      ...this.currentState,
      isUploading,
    });
  }

  private setError(error: string): void {
    this.updateState({
      ...this.currentState,
      error,
    });
  }

  private clearError(): void {
    this.updateState({
      ...this.currentState,
      error: null,
    });
  }

  private updateState(newState: FileUploadState): void {
    this.fileUploadStateSubject.next(newState);
  }

  // Observable getters for reactive programming
  get uploadedFile$(): Observable<FileUpload | undefined> {
    return new Observable((observer) => {
      const subscription = this.fileUploadState$.subscribe((state) =>
        observer.next(state.uploadedFile),
      );
      return () => subscription.unsubscribe();
    });
  }

  get isUploading$(): Observable<boolean> {
    return new Observable((observer) => {
      const subscription = this.fileUploadState$.subscribe((state) =>
        observer.next(state.isUploading),
      );
      return () => subscription.unsubscribe();
    });
  }

  get error$(): Observable<string | null> {
    return new Observable((observer) => {
      const subscription = this.fileUploadState$.subscribe((state) => observer.next(state.error));
      return () => subscription.unsubscribe();
    });
  }
}
